import type { ScoreRequestStatus } from "@/api/FinancialScoreApi";
import type { Landlord } from "@/types/PeopleTypes";
import type { UserCommunicationMethod } from "@/types/UserTypes";
import { ApiBase } from "./ApiBase";
import { LandlordHasConfirmedIdentityRequiredError } from "./Errors";

export interface LandlordProfile {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  whatsappNumber: string;
  email: string;
  preferredCommunicationMethods: UserCommunicationMethod[];
  isVerified: boolean;
  iban: string | null;
  passportNumber: string | null;
  isVerifiedByPersona: boolean;
  identityDocumentId: string | null;
  ocrVerificationStatus: string | null;
  hasConfirmedIdentityDocument: boolean;
}

export interface SubmitEmiratesIdRequest {
  emiratesIdFrontBlobName: string;
  emiratesIdBackBlobName: string;
  fullName: string;
  emiratesIdNumber: string;
  /** ISO date (yyyy-MM-dd). */
  dateOfBirth: string;
  /** ISO date (yyyy-MM-dd). */
  expirationDate: string;
  nationality: string;
}

export interface SubmitPassportRequest {
  passportBlobName: string;
  fullName: string;
  passportNumber: string;
  /** ISO date (yyyy-MM-dd). */
  dateOfBirth: string;
  /** ISO date (yyyy-MM-dd). */
  expirationDate: string;
  nationality: string;
}

export interface SubmitIdentityDocumentResponse {
  identityDocumentId: string;
}

export type GetLandlordProfileResponse = LandlordProfile;

export interface LandlordPeopleRenter {
  id: string;
  renterName: string;
  propertyAddress: string | null;
  /** The property the tenancy is on, so the row can read that lease's standing. */
  propertyId?: string | null;
  financialScore: number | null;
  status: string;
  contactEmail: string | null;
  contactPhone: string | null;
  shareToken: string | null;
  scoreRequestStatus: ScoreRequestStatus | null;
  /** The tenant rents or is renewing one of the caller's units: their contact can be set. */
  canEditContact?: boolean;
}

export interface LandlordPeopleAgent {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  totalProperties: number;
  rentedProperties: number;
}

export interface GetLandlordPeopleResponse {
  renters: LandlordPeopleRenter[];
  agents: LandlordPeopleAgent[];
  /** Every other owner on the caller's properties, one card per person. */
  owners?: Landlord[];
}

export interface UpdateLandlordProfileRequest {
  whatsappNumber: string;
  preferredCommunicationMethods: UserCommunicationMethod[];
}

export class NoLandlordProfileError extends Error {
  constructor() {
    super("No landlord profile found.");
  }
}

export class AlreadyHasIdentityDocumentError extends Error {
  constructor() {
    super("You already have an identity document.");
  }
}

export enum LandlordApiErrorCode {
  NoLandlordProfile = "NoLandlordProfile",
  AlreadyHasIdentityDocument = "AlreadyHasIdentityDocument",
}

class LandlordApi extends ApiBase {
  getProfile(): Promise<GetLandlordProfileResponse> {
    return this.get<GetLandlordProfileResponse>("/landlord/profile");
  }

  updateProfile(profile: UpdateLandlordProfileRequest): Promise<void> {
    return this.post<UpdateLandlordProfileRequest, void>(
      "/landlord/profile",
      profile,
      {
        domainErrors: {
          [LandlordApiErrorCode.NoLandlordProfile]: NoLandlordProfileError,
        },
      },
    );
  }

  updatePayoutMethod(iban: string): Promise<void> {
    return this.post<{ iban: string }, void>("/landlord/payout-method", {
      iban,
    });
  }

  syncProfileFromIdentity(): Promise<void> {
    return this.post<Record<string, never>, void>("/landlord/profile/sync", {});
  }

  /**
   * Submits the landlord's confirmed Emirates ID details. The landlord has
   * already reviewed the (OCR-prefilled or manually-entered) fields.
   */
  submitEmiratesId(
    request: SubmitEmiratesIdRequest,
  ): Promise<SubmitIdentityDocumentResponse> {
    return this.post<SubmitEmiratesIdRequest, SubmitIdentityDocumentResponse>(
      "/landlord/identity-document/emirates-id",
      request,
      {
        domainErrors: {
          [LandlordApiErrorCode.NoLandlordProfile]: NoLandlordProfileError,
          [LandlordApiErrorCode.AlreadyHasIdentityDocument]:
            AlreadyHasIdentityDocumentError,
        },
      },
    );
  }

  /**
   * Submits the landlord's confirmed passport details. The landlord has
   * already reviewed the (OCR-prefilled or manually-entered) fields.
   */
  submitPassport(
    request: SubmitPassportRequest,
  ): Promise<SubmitIdentityDocumentResponse> {
    return this.post<SubmitPassportRequest, SubmitIdentityDocumentResponse>(
      "/landlord/identity-document/passport",
      request,
      {
        domainErrors: {
          [LandlordApiErrorCode.NoLandlordProfile]: NoLandlordProfileError,
          [LandlordApiErrorCode.AlreadyHasIdentityDocument]:
            AlreadyHasIdentityDocumentError,
        },
      },
    );
  }

  async toggleTrustProtection(
    propertyId: string,
    isTrustEnabled: boolean,
  ): Promise<void> {
    await this.post<{ isTrustEnabled: boolean }, void>(
      `properties/${propertyId}/trust`,
      {
        isTrustEnabled,
      },
      {
        domainErrors: {
          LandlordHasConfirmedIdentityRequired:
            LandlordHasConfirmedIdentityRequiredError,
        },
      },
    );
  }

  /**
   * Gets the landlord's people (renters and agents)
   */
  getPeople(): Promise<GetLandlordPeopleResponse> {
    return this.get<GetLandlordPeopleResponse>("/landlord/people");
  }

  /**
   * Generates a renter invite link for the landlord (not tied to a property)
   */
}

export const landlordApi = new LandlordApi();
